import { componentSizes } from '../../theme/componentSizes';
import { r, s, spacing } from '../../theme/tokens';
import type { SxProps, Theme } from '@mui/material/styles';

type ButtonSxItem = Exclude<Extract<SxProps<Theme>, readonly unknown[]>[number], boolean>;

const isButtonSxItem = (sx: SxProps<Theme>): sx is ButtonSxItem =>
  !Array.isArray(sx) && sx !== null;

type ButtonVisualSize = 'xs' | 'sm' | 'md' | 'lg';

type ButtonSxOptions = {
  fullWidthOnMobile: boolean;
  isDestructive: boolean;
  isErrorOutlined: boolean;
  isGrey: boolean;
  isSecondary: boolean;
  loading: boolean;
  sx?: SxProps<Theme>;
  visualSize: ButtonVisualSize;
};

type ButtonContentSxOptions = {
  loading: boolean;
};

export const buttonSizeStyles: Record<
  ButtonVisualSize,
  { height: number; paddingY: string; paddingX: string; fontSize: number }
> = {
  xs: {
    height: componentSizes.buttons.xsHeight,
    paddingY: s('xs'),
    paddingX: s('md'),
    fontSize: componentSizes.buttons.xsFontSize,
  },
  sm: {
    height: componentSizes.buttons.height,
    paddingY: s('xs'),
    paddingX: s('md'),
    fontSize: componentSizes.buttons.fontSize,
  },
  md: {
    height: componentSizes.buttons.height,
    paddingY: s('sm'),
    paddingX: s('lg'),
    fontSize: componentSizes.buttons.fontSize,
  },
  lg: {
    height: componentSizes.buttons.height,
    paddingY: s('md'),
    paddingX: s('xl'),
    fontSize: componentSizes.buttons.fontSize,
  },
};

export const BUTTON_LOADING_SPINNER_SIZE = spacing.lg;

export const buildButtonSx = ({
  fullWidthOnMobile,
  isDestructive,
  isErrorOutlined,
  isGrey,
  isSecondary,
  loading,
  sx,
  visualSize,
}: ButtonSxOptions): SxProps<Theme> => {
  const style = buttonSizeStyles[visualSize];
  const buttonSx: ButtonSxItem[] = [
    {
      borderRadius: r('sm'),
      textTransform: 'none',
      fontWeight: 600,
      fontSize: style.fontSize,
      height: style.height,
      minHeight: style.height,
      paddingY: style.paddingY,
      paddingX: style.paddingX,
      position: 'relative',
      width: fullWidthOnMobile ? { xs: '100%', sm: 'auto' } : 'auto',
      minWidth: 'auto',
      ...(loading
        ? {
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
              visibility: 'hidden',
            },
          }
        : {}),
      borderColor:
        isSecondary && !isDestructive && !isGrey
          ? isErrorOutlined
            ? 'error.main'
            : 'primary.main'
          : undefined,
      '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: s('xs') },
    },
  ];

  if (isGrey) {
    buttonSx.push({
      '&&': {
        borderColor: 'divider',
        color: 'text.secondary',
      },
      '&&:hover': {
        borderColor: 'text.secondary',
        color: 'text.primary',
        backgroundColor: 'action.hover',
      },
    });
  }

  if (isErrorOutlined && !isDestructive) {
    buttonSx.push({
      '&&': {
        borderColor: 'error.main',
        color: 'error.main',
      },
      '&&:hover': {
        borderColor: 'error.main',
        color: 'error.main',
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
      },
    });
  }

  if (isDestructive) {
    buttonSx.push({
      '&&': {
        borderColor: 'error.main',
        color: 'error.main',
      },
      '&&:hover': {
        borderColor: 'error.main',
        color: 'error.main',
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
      },
    });
  }

  if (sx) {
    if (Array.isArray(sx)) {
      const sxItems = sx.filter((item): item is ButtonSxItem => Boolean(item));
      buttonSx.push(...sxItems);
    } else if (isButtonSxItem(sx)) {
      buttonSx.push(sx);
    }
  }

  return buttonSx;
};

export const buildButtonContentSx = ({ loading }: ButtonContentSxOptions): SxProps<Theme> => ({
  visibility: loading ? 'hidden' : 'visible',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

export const BUTTON_LOADING_OVERLAY_SX = {
  position: 'absolute',
  inset: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
} as const;
